import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight, DollarSign, MapPin,
  CircleHelp, Sparkles, TrendingUp, TriangleAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  Popover, PopoverContent, PopoverDescription, PopoverHeader, PopoverTitle, PopoverTrigger,
} from '@/components/ui/popover';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import DealHeader from '@/components/deal/DealHeader';
import useDealFlow from '@/hooks/useDealFlow';
import { cn } from '@/lib/utils';
import {
  DEAL_STAGES_V2,
  DEFAULT_CPM_CAP,
  FLIGHT_OPTIONS,
  INVENTORY_TIER_GUIDE,
  OFFER_BUNDLES,
  PRICING_TIERS,
  buildTargetAreaSummary,
  computeFlightEnd,
  computePricing,
  estimateReachFromOffer,
  formatDollars,
  getInventoryTierForCpm,
  scoreOfferBundles,
} from '@/components/deal/mockDealData';

const BUDGET_MIN = 50;
const BUDGET_MAX = Math.max(...OFFER_BUNDLES.map((bundle) => bundle.investmentRange.max));
const CPM_MIN = INVENTORY_TIER_GUIDE[0].minCpm;
const CPM_MAX = INVENTORY_TIER_GUIDE[INVENTORY_TIER_GUIDE.length - 1].maxCpm;
const HIGHLIGHT_SECTIONS = ['delivery', 'budget', 'target', 'markup'];

const TEMPLATE_DEFAULTS = {
  'bundle-starter-2w': { cpmCap: 20, markupTierId: 'tier-standard', targetMode: 'hyper_local', radius: 4 },
  'bundle-burst-2w': { cpmCap: 26, markupTierId: 'tier-premium', targetMode: 'standard_area', radius: 10 },
  'bundle-growth-4w': { cpmCap: 30, markupTierId: 'tier-standard', targetMode: 'standard_area', radius: 14 },
  'bundle-brand-8w': { cpmCap: 36, markupTierId: 'tier-premium', targetMode: 'standard_area', radius: 18 },
};

function clamp(value, min, max) {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return min;
  return Math.min(max, Math.max(min, numeric));
}

function normalizeTargetMode(mode, hasLocation) {
  switch (mode) {
    case 'around_location':
      return 'standard_area';
    case 'custom_address':
      return 'radius';
    case 'city_wide':
      return 'standard_area';
    case 'multi_store':
      return 'zip_codes';
    case 'hyper_local':
    case 'standard_area':
    case 'radius':
    case 'zip_codes':
      return mode;
    default:
      return hasLocation ? 'standard_area' : 'hyper_local';
  }
}

function getTemplateDefaults(bundleId) {
  return TEMPLATE_DEFAULTS[bundleId] || {
    cpmCap: DEFAULT_CPM_CAP,
    markupTierId: 'tier-standard',
    targetMode: 'standard_area',
    radius: 10,
  };
}

function createCustomPricing(platformCost, customSellPrice) {
  const safeCost = Number(platformCost) || 0;
  const safeSellPrice = Number(customSellPrice) || 0;
  const sellerEarnings = safeSellPrice - safeCost;
  return {
    sellPrice: safeSellPrice,
    platformCost: safeCost,
    sellerEarnings,
    marginPct: safeSellPrice > 0 ? sellerEarnings / safeSellPrice : 0,
    markupPct: safeCost > 0 ? sellerEarnings / safeCost : 0,
    tierId: 'custom',
  };
}

function getMarkupSummary(selectedTierId, pricingTier, pricing) {
  const label = selectedTierId === 'custom' ? 'Custom' : pricingTier.label;
  return `${label} · ${formatDollars(pricing.sellPrice)} client price`;
}

function formatSingleViewCost(cpm) {
  const singleViewCost = (Number(cpm) || 0) / 1000;
  if (singleViewCost <= 0) return '$0';
  const cents = singleViewCost * 100;
  if (cents < 10) return `${cents.toFixed(1)}¢`;
  if (cents < 100) return `${Math.round(cents)}¢`;
  return formatDollars(singleViewCost);
}

// Inline label + (?) help — surfaces the same explainer copy that lives in the
// "How pricing works" sheet, so a non-expert seller doesn't have to discover it.
function LabelWithHelp({ label, help }) {
  return (
    <span className="inline-flex items-center gap-1 text-slate-500">
      {label}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`What does "${label}" mean?`}
            className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
          >
            <CircleHelp className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent side="top" className="w-72 text-xs leading-5 text-slate-600">
          {help}
        </PopoverContent>
      </Popover>
    </span>
  );
}

export default function OfferBuilder() {
  const { orgSlug, dealId } = useParams();
  const navigate = useNavigate();
  const deal = useDealFlow();
  const businessId = deal.activeBusinessId || deal.businessProfile?.id;
  const highlightTimeoutRef = useRef(null);

  const hasKnownLocation = Boolean(deal.businessProfile?.address?.trim());
  const existingTargetMode = normalizeTargetMode(deal.selectedPackage?.targetArea, hasKnownLocation);
  const locationSeed = deal.selectedPackage?.targetCenter || deal.businessProfile?.address || '';
  const offerContext = useMemo(() => ({
    objective: deal.creativeSet?.objective
      || (deal.creatives?.[deal.activeBusinessId] || []).find((creative) => creative.objective)?.objective
      || null,
    triggerEvent: deal.lead?.triggerEvent || null,
    category: deal.businessProfile?.category || null,
    scopeType: deal.scope?.type || deal.businesses?.[deal.activeBusinessId]?.scope || null,
    businessName: deal.businessProfile?.name || null,
  }), [
    deal.activeBusinessId,
    deal.businessProfile?.category,
    deal.businessProfile?.name,
    deal.businesses,
    deal.creativeSet?.objective,
    deal.creatives,
    deal.lead?.triggerEvent,
    deal.scope?.type,
  ]);

  const contextTemplates = useMemo(
    () => scoreOfferBundles(offerContext),
    [offerContext],
  );
  const recommendedTemplate = contextTemplates[0] || OFFER_BUNDLES[0];
  const initialTemplateId = deal.selectedPackage?.bundleId || recommendedTemplate.id;
  const initialTemplateDefaults = getTemplateDefaults(initialTemplateId);
  const initialTierId = deal.selectedPackage?.pricing?.tierId || initialTemplateDefaults.markupTierId;

  const [selectedTemplateId, setSelectedTemplateId] = useState(initialTemplateId);
  const [budget, setBudget] = useState(
    clamp(
      deal.selectedPackage?.budget || recommendedTemplate.investmentRange.default,
      BUDGET_MIN,
      BUDGET_MAX,
    ),
  );
  const [cpmCap, setCpmCap] = useState(
    clamp(deal.selectedPackage?.cpmCap || initialTemplateDefaults.cpmCap, CPM_MIN, CPM_MAX),
  );
  const [flightWeeks, setFlightWeeks] = useState(
    deal.selectedPackage?.duration?.weeks || deal.selectedPackage?.flight || recommendedTemplate.duration.weeks,
  );
  const [targetMode, setTargetMode] = useState(existingTargetMode);
  const [targetModeView, setTargetModeView] = useState(
    ['radius', 'zip_codes'].includes(existingTargetMode) ? 'advanced' : 'simple',
  );
  const [centerLocation, setCenterLocation] = useState(locationSeed);
  const [radius, setRadius] = useState(
    clamp(
      deal.selectedPackage?.radius || initialTemplateDefaults.radius || (existingTargetMode === 'hyper_local' ? 4 : 10),
      3,
      25,
    ),
  );
  const [zipCodes, setZipCodes] = useState(deal.selectedPackage?.targetZips || '');
  const [selectedTierId, setSelectedTierId] = useState(initialTierId);
  const [customSellPrice, setCustomSellPrice] = useState(
    initialTierId === 'custom' ? String(deal.selectedPackage?.pricing?.sellPrice || '') : '',
  );
  const [openSection, setOpenSection] = useState('delivery');
  const [highlightedSections, setHighlightedSections] = useState([]);
  const [mobileSummarySheetOpen, setMobileSummarySheetOpen] = useState(false);
  // F39/F59 — Seller-view/Client-view modulation: same data, different framings.
  // Client view hides internal economics (Campaign cost, Seller earnings,
  // Markup) so the seller can rehearse the buyer's-eye-view of the offer.
  const [summaryView, setSummaryView] = useState('seller');
  const isClientView = summaryView === 'client';
  const [mobilePricingHelpOpen, setMobilePricingHelpOpen] = useState(false);

  useEffect(() => {
    deal.setCurrentStage('offer');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (!contextTemplates.find((bundle) => bundle.id === selectedTemplateId) && contextTemplates[0]) {
      setSelectedTemplateId(contextTemplates[0].id);
    }
  }, [contextTemplates, selectedTemplateId]);

  // Auto-fill CENTER LOCATION from the business profile address when it loads
  // asynchronously after mount. Only seed when the user hasn't typed anything
  // and there's no preselected package targetCenter already in play. The
  // address can live on either businessProfile or businesses[activeId].
  useEffect(() => {
    if (centerLocation.trim()) return;
    if (deal.selectedPackage?.targetCenter) return;
    const profileAddress = deal.businessProfile?.address?.trim()
      || deal.businesses?.[deal.activeBusinessId]?.address?.trim();
    if (profileAddress) {
      setCenterLocation(profileAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.businessProfile?.address, deal.businesses, deal.activeBusinessId]);

  const selectedTemplate = contextTemplates.find((bundle) => bundle.id === selectedTemplateId) || contextTemplates[0];
  const inventoryTier = getInventoryTierForCpm(cpmCap);
  const selectedFlight = FLIGHT_OPTIONS.find((option) => option.weeks === flightWeeks)
    || { weeks: flightWeeks, label: `${flightWeeks} weeks` };
  const targetSummary = useMemo(
    () => buildTargetAreaSummary({
      targetMode,
      center: centerLocation,
      radius,
      zipCodes,
      businessName: deal.businessProfile?.name,
    }),
    [centerLocation, deal.businessProfile?.name, radius, targetMode, zipCodes],
  );
  const reachEstimate = useMemo(
    () => estimateReachFromOffer({
      budget,
      cpmCap,
      targetMode,
      radius,
      zipCodes,
      flightWeeks,
    }),
    [budget, cpmCap, flightWeeks, radius, targetMode, zipCodes],
  );

  const pricingTier = PRICING_TIERS.find((tier) => tier.id === selectedTierId) || PRICING_TIERS[1];
  const pricing = selectedTierId === 'custom'
    ? createCustomPricing(budget, customSellPrice)
    : { ...computePricing(budget, pricingTier), tierId: pricingTier.id };
  const customPriceTooLow = selectedTierId === 'custom'
    && customSellPrice !== ''
    && Number(customSellPrice) < budget;
  const singleViewCost = formatSingleViewCost(cpmCap);

  const requiredLocation = targetMode !== 'zip_codes';
  const hasTargetingInput = targetMode === 'zip_codes'
    ? zipCodes.split(',').map((zip) => zip.trim()).filter(Boolean).length > 0
    : centerLocation.trim().length > 0;
  const canContinue = budget >= BUDGET_MIN && hasTargetingInput && !customPriceTooLow
    && (selectedTierId !== 'custom' || Number(customSellPrice) > 0);
  const templateSummary = selectedTemplate
    ? `${formatDollars(selectedTemplate.investmentRange.default)} · ${selectedTemplate.duration.label}`
    : '';
  const deliverySummary = `$${cpmCap} per 1,000 ad views · ${inventoryTier.shortLabel}`;
  const budgetSummary = `${formatDollars(budget)} · ${selectedFlight.label}`;
  const markupSummary = getMarkupSummary(selectedTierId, pricingTier, pricing);
  const nextEndDate = computeFlightEnd(new Date(), flightWeeks);
  const selectedTemplateDefaults = selectedTemplate ? getTemplateDefaults(selectedTemplate.id) : initialTemplateDefaults;
  const isTemplateCustomized = !!selectedTemplate && (
    budget !== selectedTemplate.investmentRange.default
    || flightWeeks !== selectedTemplate.duration.weeks
    || cpmCap !== selectedTemplateDefaults.cpmCap
    || targetMode !== selectedTemplateDefaults.targetMode
    || (targetMode !== 'zip_codes' && radius !== selectedTemplateDefaults.radius)
    || selectedTierId !== selectedTemplateDefaults.markupTierId
  );

  const flashSections = (sections) => {
    setHighlightedSections(sections);
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = setTimeout(() => setHighlightedSections([]), 1400);
  };

  const pricingHelpContent = (
    <div className="space-y-4">
      <div className="rounded-[1.2rem] border border-slate-200 bg-slate-50 p-3.5">
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">Current example</p>
        <div className="mt-3 grid gap-2.5 sm:grid-cols-3">
          <div>
            <p className="text-xs text-slate-500">Campaign cost</p>
            <p className="mt-1 text-base font-semibold text-slate-950">{formatDollars(pricing.platformCost)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Client price</p>
            <p className="mt-1 text-base font-semibold text-slate-950">{formatDollars(pricing.sellPrice)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Seller earnings</p>
            <p className="mt-1 text-base font-semibold text-slate-950">{formatDollars(pricing.sellerEarnings)}</p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="space-y-1.5">
          <p className="text-sm font-semibold text-slate-950">Ad-view price</p>
          <p className="text-sm leading-5 text-slate-600">
            This is the media cost for 1,000 ad views. In media buying, this is called CPM. At the current setting,
            that works out to about {singleViewCost} for one ad view.
          </p>
        </div>

        <div className="space-y-1.5">
          <p className="text-sm font-semibold text-slate-950">Budget</p>
          <p className="text-sm leading-5 text-slate-600">
            This is the campaign cost to run the media. It is what the platform spends before any markup is added.
          </p>
        </div>

        <div className="space-y-1.5">
          <p className="text-sm font-semibold text-slate-950">Markup</p>
          <p className="text-sm leading-5 text-slate-600">
            Markup is what you add on top of campaign cost when setting the client price. It covers your commercial margin.
          </p>
        </div>

        <div className="space-y-1.5">
          <p className="text-sm font-semibold text-slate-950">Reach estimates</p>
          <p className="text-sm leading-5 text-slate-600">
            Ad views and households are planning estimates, not guarantees. Final delivery depends on geography, pacing, and available inventory.
          </p>
        </div>
      </div>
    </div>
  );

  const handleApplyTemplate = (templateId) => {
    const template = contextTemplates.find((candidate) => candidate.id === templateId);
    if (!template) return;

    const defaults = getTemplateDefaults(templateId);
    setSelectedTemplateId(templateId);
    setBudget(template.investmentRange.default);
    setFlightWeeks(template.duration.weeks);
    setCpmCap(defaults.cpmCap);
    setSelectedTierId(defaults.markupTierId);
    setCustomSellPrice('');
    setRadius(defaults.radius);
    setTargetMode(defaults.targetMode);
    setTargetModeView(['radius', 'zip_codes'].includes(defaults.targetMode) ? 'advanced' : 'simple');
    setOpenSection('delivery');
    flashSections(HIGHLIGHT_SECTIONS);
  };

  const handleTargetModeViewChange = (value) => {
    setTargetModeView(value);
    if (value === 'simple' && ['radius', 'zip_codes'].includes(targetMode)) {
      setTargetMode(hasKnownLocation ? 'standard_area' : 'hyper_local');
    }
    if (value === 'advanced' && ['hyper_local', 'standard_area'].includes(targetMode)) {
      setTargetMode('radius');
    }
  };

  const handleContinue = async () => {
    if (!canContinue || !selectedTemplate) return;

    const nextPackage = {
      ...deal.selectedPackage,
      id: selectedTemplate.id,
      bundleId: selectedTemplate.id,
      name: selectedTemplate.name,
      budget,
      cpmCap,
      inventoryTier: inventoryTier.id,
      flight: flightWeeks,
      duration: selectedFlight,
      targetArea: targetMode,
      targetAddress: targetSummary.label,
      targetCenter: centerLocation.trim(),
      targetZips: targetMode === 'zip_codes' ? zipCodes.trim() : '',
      radius: ['hyper_local', 'standard_area', 'radius'].includes(targetMode) ? radius : null,
      reachEstimate,
      pricing: {
        ...pricing,
        tierId: selectedTierId,
      },
    };

    deal.setSelectedPackage(nextPackage);

    if (!businessId) {
      navigate(`/org/${orgSlug}/deal/${dealId}/proposal`);
      return;
    }

    try {
      await deal.saveOfferConfigForBusiness(businessId, nextPackage);
      const draftProposal = await deal.ensureDraftProposalForBusiness(businessId, {
        package_name: nextPackage.name || null,
      });
      if (draftProposal) {
        deal.setProposal(draftProposal);
      }
      navigate(`/org/${orgSlug}/deal/${dealId}/proposal`);
    } catch (error) {
      console.error('Failed to save offer config', error);
      navigate(`/org/${orgSlug}/deal/${dealId}/proposal`);
    }
  };

  return (
    <>
      <DealHeader
        businessName={deal.businessProfile?.name}
        currentStage="offer"
        stages={DEAL_STAGES_V2}
        onBack={() => {
          const businessId = deal.activeBusinessId || deal.businessProfile?.id;
          if (businessId) navigate(`/org/${orgSlug}/deal/business/${businessId}`);
          else navigate(-1);
        }}
      />

      <div className="mx-auto max-w-7xl min-w-0 overflow-x-hidden px-4 py-6 pb-28 sm:px-6 sm:py-8 xl:pb-8">
        <div className="mb-5">
          <Badge variant="outline" className="rounded-full border-slate-200 bg-white text-[11px] tracking-[0.2em] text-slate-600">
            SHAPE OFFER
          </Badge>
          <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-950 sm:text-4xl">
            Build the commercial offer
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Start with a template, then adjust only what matters.
          </p>
        </div>

        <div className="xl:hidden">
          <div className="min-w-0 overflow-hidden rounded-[1.35rem] border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">Current offer</p>
                <h2 className="mt-2 line-clamp-2 text-lg font-semibold leading-tight text-slate-950">
                  {selectedTemplate?.name || 'Campaign offer'}
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  {formatDollars(pricing.sellPrice)} client price
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Sheet open={mobilePricingHelpOpen} onOpenChange={setMobilePricingHelpOpen}>
                  <SheetTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 rounded-full border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      aria-label="Learn how pricing works"
                    >
                      <CircleHelp className="h-4 w-4" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="bottom" className="h-auto max-h-[88dvh] rounded-t-[1.75rem] border-slate-200 px-4 pb-6 pt-5">
                    <SheetHeader className="p-0 text-left">
                      <SheetTitle className="text-lg text-slate-950">How pricing works</SheetTitle>
                      <SheetDescription className="text-sm text-slate-600">
                        A quick commercial breakdown for the seller side of the offer.
                      </SheetDescription>
                    </SheetHeader>
                    <div className="mt-5">{pricingHelpContent}</div>
                  </SheetContent>
                </Sheet>

                <Badge variant="outline" className="shrink-0 rounded-full border-slate-200 bg-slate-50 text-xs text-slate-600">
                  {selectedFlight.label}
                </Badge>
              </div>
            </div>

            <div className="mt-4 grid gap-2.5 min-[360px]:grid-cols-2">
              <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">Ad views</p>
                <p className="mt-1.5 text-lg font-semibold text-slate-950">{reachEstimate.maxImpressions.toLocaleString()}</p>
              </div>
              <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">Households</p>
                <p className="mt-1.5 text-lg font-semibold text-slate-950">{reachEstimate.estimatedHouseholds.toLocaleString()}</p>
              </div>
            </div>

            <Sheet open={mobileSummarySheetOpen} onOpenChange={setMobileSummarySheetOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className="mt-3 h-10 w-full rounded-xl border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                >
                  View offer details
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-auto max-h-[88dvh] rounded-t-[1.75rem] border-slate-200 px-4 pb-6 pt-5">
                <SheetHeader className="p-0 text-left">
                  <SheetTitle className="text-lg text-slate-950">Full summary</SheetTitle>
                  <SheetDescription className="text-sm text-slate-600">
                    The numbers behind this offer. Tap any (?) for a plain-English explanation.
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-5 space-y-3 rounded-[1.35rem] border border-slate-200 bg-slate-50 p-4 text-sm">
                  <div className="flex items-start justify-between gap-4">
                    <span className="text-slate-500">Target area</span>
                    <span className="max-w-[13rem] break-words text-right font-medium leading-5 text-slate-950">{targetSummary.label}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <LabelWithHelp label="Daily pacing" help="How many ad views are projected to deliver each day, on average. Pacing depends on geography, the ad-view price you set, and available streaming inventory." />
                    <span className="font-medium text-slate-950">{reachEstimate.dailyImpressions.toLocaleString()} / day</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <LabelWithHelp label="Unique reach" help="The estimated number of distinct people who will see the ad at least once. Different from ad views, which counts every time the ad plays." />
                    <span className="font-medium text-slate-950">{reachEstimate.estimatedUniqueReach.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <LabelWithHelp label="Campaign cost" help="What the platform spends to deliver the media before any markup. The client price minus campaign cost is your seller earnings." />
                    <span className="font-medium text-slate-950">{formatDollars(pricing.platformCost)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-slate-500">Seller earnings</span>
                    <span className="font-medium text-slate-950">{formatDollars(pricing.sellerEarnings)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-slate-500">Markup</span>
                    <span className="font-medium text-slate-950">{Math.round((pricing.markupPct || 0) * 100)}%</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-slate-500">End date</span>
                    <span className="font-medium text-slate-950">{nextEndDate}</span>
                  </div>
                  <p className="pt-1 text-sm leading-5 text-slate-600">{reachEstimate.note}</p>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="mt-4 grid min-w-0 gap-4 xl:mt-0 xl:grid-cols-[1.28fr_0.92fr] xl:items-start">
          <div className="order-1 min-w-0 space-y-4 sm:space-y-6">
            <Card className="overflow-hidden border-slate-200 shadow-sm">
              <CardContent className="p-4 sm:p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                      <Sparkles className="h-3.5 w-3.5 text-coral" />
                      Starting templates
                    </div>
                    <h2 className="mt-2 text-base font-semibold text-slate-950">Pick a starting point</h2>
                  </div>
                  {selectedTemplate && (
                    <Badge variant="outline" className="w-fit rounded-full border-slate-200 bg-slate-50 text-[11px] text-slate-600">
                      <span className="sm:hidden">
                        {isTemplateCustomized ? 'Customized template' : 'Template selected'}
                      </span>
                      <span className="hidden sm:inline">
                        {isTemplateCustomized ? `Based on ${selectedTemplate.name} · Customized` : `${selectedTemplate.name} selected`}
                      </span>
                    </Badge>
                  )}
                </div>

                <div className="mt-3 -mx-1 flex snap-x snap-mandatory gap-2.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {contextTemplates.map((template) => {
                    const Icon = template.icon;
                    const isSelected = template.id === selectedTemplateId;
                    return (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => handleApplyTemplate(template.id)}
                        className={cn(
                          'w-[14rem] min-w-[14rem] max-w-[calc(100vw-4.5rem)] snap-start rounded-[1.35rem] border px-3.5 py-3 text-left transition-colors lg:min-w-[13.5rem] lg:w-[13.5rem]',
                          isSelected
                            ? 'border-coral bg-coral/[0.06]'
                            : 'border-slate-200 bg-white hover:border-slate-300',
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className={cn(
                            'flex h-8 w-8 items-center justify-center rounded-2xl',
                            isSelected ? 'bg-coral/10' : 'bg-slate-100',
                          )}>
                            <Icon className={cn('h-4 w-4', isSelected ? 'text-coral' : 'text-slate-500')} />
                          </div>
                          <div className="flex flex-wrap justify-end gap-2">
                            {template.isRecommended && (
                              <Badge className="border-0 bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700 shadow-none">Recommended</Badge>
                            )}
                            {isSelected && (
                              <Badge className="border-0 bg-slate-950 px-2 py-0.5 text-[11px] text-white shadow-none">
                                {isTemplateCustomized ? 'Customized' : 'Selected'}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className="mt-3 text-sm font-semibold text-slate-950">{template.name}</p>
                        <p className="mt-1 text-sm text-slate-500">
                          {templateSummary && isSelected ? templateSummary : `${formatDollars(template.investmentRange.default)} · ${template.duration.label}`}
                        </p>
                        <p className="mt-2 line-clamp-2 text-sm leading-5 text-slate-600">{template.tagline}</p>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden border-slate-200 shadow-sm">
              <CardContent className="p-0">
                <Accordion
                  type="single"
                  collapsible
                  value={openSection}
                  onValueChange={(value) => setOpenSection(value || '')}
                  className="w-full"
                >
                  <AccordionItem
                    value="delivery"
                    className={cn(
                      'border-b border-slate-200 px-4 sm:px-5',
                      highlightedSections.includes('delivery') && 'bg-coral/[0.05] transition-colors',
                    )}
                  >
                    <AccordionTrigger className="py-3.5 sm:py-4 hover:no-underline">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                          <TrendingUp className="h-3.5 w-3.5 text-coral" />
                          Delivery quality
                        </div>
                        <div className="mt-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                          <h3 className="text-base font-semibold text-slate-950">Choose the ad-view price</h3>
                          <p className="text-sm text-slate-600">{deliverySummary}</p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="min-w-0 pb-4">
                      <div className="space-y-4">
                        <div className="min-w-0 rounded-[1.4rem] border border-slate-200 bg-slate-50 p-3.5">
                          <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                            <div className="min-w-0">
                              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">Current price</p>
                              <p className="mt-1 text-2xl font-semibold text-slate-950">${cpmCap}</p>
                              <p className="text-sm text-slate-500">{inventoryTier.label}</p>
                              <p className="mt-1 text-xs text-slate-500">About {singleViewCost} per ad view</p>
                            </div>
                            <p className="min-w-0 text-sm leading-5 text-slate-500 sm:max-w-[12rem] sm:text-right">
                              Lower pricing buys more volume. Higher pricing favors more selective, premium placements.
                            </p>
                          </div>

                          {/* Tier pills are the primary affordance — click to set
                              cpmCap to the tier's midpoint. The slider + anchor
                              labels live behind a "Fine-tune" toggle for sellers
                              who want pixel-perfect control. */}
                          <div className="mt-4 flex flex-wrap gap-2">
                            {INVENTORY_TIER_GUIDE.map((tier) => {
                              const isActive = tier.id === inventoryTier.id;
                              const tierMidpoint = Math.round((tier.minCpm + tier.maxCpm) / 2);
                              return (
                                <button
                                  key={tier.id}
                                  type="button"
                                  onClick={() => setCpmCap(tierMidpoint)}
                                  className={cn(
                                    'rounded-full border px-3 py-1.5 text-sm transition-colors',
                                    isActive
                                      ? 'border-coral bg-coral/[0.06]'
                                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50',
                                  )}
                                >
                                  <span className="font-medium text-slate-950">{tier.shortLabel}</span>
                                  <span className="ml-2 text-xs text-slate-500">${tier.minCpm}-${tier.maxCpm}</span>
                                </button>
                              );
                            })}
                          </div>

                          <details className="mt-3 group">
                            <summary className="cursor-pointer list-none text-xs font-medium text-slate-500 hover:text-slate-700">
                              <span className="group-open:hidden">Fine-tune the price ↓</span>
                              <span className="hidden group-open:inline">Hide fine-tune</span>
                            </summary>
                            <Slider
                              value={[cpmCap]}
                              onValueChange={([value]) => setCpmCap(clamp(value, CPM_MIN, CPM_MAX))}
                              min={CPM_MIN}
                              max={CPM_MAX}
                              step={1}
                              className="mt-3 [&_[data-slot=slider-range]]:bg-coral [&_[data-slot=slider-thumb]]:border-coral"
                            />
                            <div className="mt-2 flex flex-col gap-1.5 text-xs text-slate-500 min-[360px]:flex-row min-[360px]:justify-between">
                              <span>Broader mix across more streaming inventory</span>
                              <span className="min-[360px]:text-right">More selective premium placements</span>
                            </div>
                          </details>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem
                    value="budget"
                    className={cn(
                      'border-b border-slate-200 px-4 sm:px-5',
                      highlightedSections.includes('budget') && 'bg-coral/[0.05] transition-colors',
                    )}
                  >
                    <AccordionTrigger className="py-3.5 sm:py-4 hover:no-underline">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                          <DollarSign className="h-3.5 w-3.5 text-emerald-600" />
                          Budget & flight
                        </div>
                        <div className="mt-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                          <h3 className="text-base font-semibold text-slate-950">Set spend and duration</h3>
                          <p className="text-sm text-slate-600">{budgetSummary}</p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="min-w-0 pb-4">
                      <div className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
                        <div className="min-w-0 rounded-[1.4rem] border border-slate-200 bg-slate-50 p-3.5">
                          <Label htmlFor="offer-budget" className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                            Budget
                          </Label>
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-2xl font-semibold text-slate-950">$</span>
                            <Input
                              id="offer-budget"
                              type="number"
                              min={BUDGET_MIN}
                              max={BUDGET_MAX}
                              step={50}
                              value={budget}
                              onChange={(event) => setBudget(clamp(event.target.value || BUDGET_MIN, BUDGET_MIN, BUDGET_MAX))}
                              className="h-11 w-full max-w-[11rem] border-slate-200 text-lg font-semibold"
                            />
                          </div>
                          <Slider
                            value={[budget]}
                            onValueChange={([value]) => setBudget(clamp(value, BUDGET_MIN, BUDGET_MAX))}
                            min={BUDGET_MIN}
                            max={BUDGET_MAX}
                            step={50}
                            className="mt-4 [&_[data-slot=slider-range]]:bg-coral [&_[data-slot=slider-thumb]]:border-coral"
                          />
                          <div className="mt-2 flex justify-between text-xs text-slate-500">
                            <span>{formatDollars(BUDGET_MIN)}</span>
                            <span>{formatDollars(BUDGET_MAX)}</span>
                          </div>
                        </div>

                        <div className="min-w-0 rounded-[1.4rem] border border-slate-200 bg-slate-50 p-3.5">
                          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">Flight</p>
                          <div className="mt-2 grid grid-cols-2 gap-2.5">
                            {FLIGHT_OPTIONS.map((option) => {
                              const isActive = flightWeeks === option.weeks;
                              return (
                                <button
                                  key={option.weeks}
                                  type="button"
                                  onClick={() => setFlightWeeks(option.weeks)}
                                  className={cn(
                                    'rounded-2xl border px-3.5 py-2.5 text-left transition-colors',
                                    isActive
                                      ? 'border-slate-950 bg-slate-950 text-white'
                                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300',
                                  )}
                                >
                                  <p className="text-sm font-semibold">{option.label}</p>
                                  <p className={cn('mt-1 text-xs', isActive ? 'text-white/70' : 'text-slate-500')}>
                                    Ends {nextEndDate}
                                  </p>
                                </button>
                              );
                            })}
                          </div>
                          <p className="mt-3 text-sm leading-5 text-slate-500">
                            Shorter flights deliver faster. Longer flights pace more smoothly.
                          </p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem
                    value="target"
                    className={cn(
                      'border-b border-slate-200 px-4 sm:px-5',
                      highlightedSections.includes('target') && 'bg-coral/[0.05] transition-colors',
                    )}
                  >
                    <AccordionTrigger className="py-3.5 sm:py-4 hover:no-underline">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                          <MapPin className="h-3.5 w-3.5 text-coral" />
                          Target area
                        </div>
                        <div className="mt-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                          <h3 className="text-base font-semibold text-slate-950">Choose where to deliver</h3>
                          <p className="text-sm text-slate-600">{targetSummary.label}</p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="min-w-0 pb-4">
                      <div className="space-y-4">
                        <div className="min-w-0 rounded-[1.4rem] border border-slate-200 bg-slate-50 p-3.5">
                          <Label htmlFor="center-location" className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                            Center location
                          </Label>
                          <Input
                            id="center-location"
                            value={centerLocation}
                            onChange={(event) => setCenterLocation(event.target.value)}
                            placeholder="Enter an address, neighborhood, or city"
                            className="mt-3 border-slate-200 bg-white"
                          />
                        </div>

                        <Tabs value={targetModeView} onValueChange={handleTargetModeViewChange}>
                          <TabsList className="grid w-full grid-cols-2 rounded-2xl bg-slate-100 p-1">
                            <TabsTrigger value="simple" className="rounded-xl px-4">Simple</TabsTrigger>
                            <TabsTrigger value="advanced" className="rounded-xl px-4">Advanced</TabsTrigger>
                          </TabsList>

                          <TabsContent value="simple" className="mt-4">
                            <div className="grid gap-3 min-[420px]:grid-cols-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setTargetMode('hyper_local');
                                  setRadius(4);
                                }}
                                className={cn(
                                  'rounded-[1.4rem] border p-3.5 text-left transition-colors',
                                  targetMode === 'hyper_local'
                                    ? 'border-coral bg-coral/[0.06]'
                                    : 'border-slate-200 bg-white hover:border-slate-300',
                                )}
                              >
                                <p className="text-sm font-semibold text-slate-950">Hyper-local</p>
                                <p className="mt-1.5 text-sm leading-5 text-slate-600">
                                  Tight delivery around the immediate surroundings.
                                </p>
                                <p className="mt-2 text-xs font-medium text-amber-700">
                                  Limited reach and slower pacing.
                                </p>
                              </button>

                              <button
                                type="button"
                                onClick={() => {
                                  setTargetMode('standard_area');
                                  setRadius(12);
                                }}
                                className={cn(
                                  'rounded-[1.4rem] border p-3.5 text-left transition-colors',
                                  targetMode === 'standard_area'
                                    ? 'border-coral bg-coral/[0.06]'
                                    : 'border-slate-200 bg-white hover:border-slate-300',
                                )}
                              >
                                <p className="text-sm font-semibold text-slate-950">Standard area</p>
                                <p className="mt-1.5 text-sm leading-5 text-slate-600">
                                  A city-wide delivery area with more reach.
                                </p>
                                <p className="mt-2 text-xs font-medium text-slate-500">
                                  Best default for pacing and scale.
                                </p>
                              </button>
                            </div>
                          </TabsContent>

                          <TabsContent value="advanced" className="mt-4 space-y-4">
                            <div className="grid gap-3 min-[420px]:grid-cols-2">
                              <button
                                type="button"
                                onClick={() => setTargetMode('radius')}
                                className={cn(
                                  'rounded-[1.4rem] border p-3.5 text-left transition-colors',
                                  targetMode === 'radius'
                                    ? 'border-coral bg-coral/[0.06]'
                                    : 'border-slate-200 bg-white hover:border-slate-300',
                                )}
                              >
                                <p className="text-sm font-semibold text-slate-950">Radius</p>
                                <p className="mt-1.5 text-sm leading-5 text-slate-600">
                                  Set a custom mile radius around the center.
                                </p>
                              </button>

                              <button
                                type="button"
                                onClick={() => setTargetMode('zip_codes')}
                                className={cn(
                                  'rounded-[1.4rem] border p-3.5 text-left transition-colors',
                                  targetMode === 'zip_codes'
                                    ? 'border-coral bg-coral/[0.06]'
                                    : 'border-slate-200 bg-white hover:border-slate-300',
                                )}
                              >
                                <p className="text-sm font-semibold text-slate-950">ZIP codes</p>
                                <p className="mt-1.5 text-sm leading-5 text-slate-600">
                                  Use exact ZIPs when the delivery map is already known.
                                </p>
                              </button>
                            </div>

                            {targetMode === 'radius' && (
                              <div className="rounded-[1.4rem] border border-slate-200 bg-slate-50 p-3.5">
                                <div className="flex items-center justify-between gap-4">
                                  <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">Radius</p>
                                  <p className="text-sm font-semibold text-slate-950">{radius} miles</p>
                                </div>
                                <Slider
                                  value={[radius]}
                                  onValueChange={([value]) => setRadius(clamp(value, 3, 25))}
                                  min={3}
                                  max={25}
                                  step={1}
                                  className="mt-4 [&_[data-slot=slider-range]]:bg-coral [&_[data-slot=slider-thumb]]:border-coral"
                                />
                              </div>
                            )}

                            {targetMode === 'zip_codes' && (
                              <div className="rounded-[1.4rem] border border-slate-200 bg-slate-50 p-3.5">
                                <Label htmlFor="zip-codes" className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                                  ZIP codes
                                </Label>
                                <Input
                                  id="zip-codes"
                                  value={zipCodes}
                                  onChange={(event) => setZipCodes(event.target.value)}
                                  placeholder="60614, 60647, 60622"
                                  className="mt-3 border-slate-200 bg-white"
                                />
                              </div>
                            )}
                          </TabsContent>
                        </Tabs>

                        {requiredLocation && !centerLocation.trim() && (
                          <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                            Add a center location to continue.
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem
                    value="markup"
                    className={cn(
                      'border-b-0 px-4 sm:px-5',
                      highlightedSections.includes('markup') && 'bg-coral/[0.05] transition-colors',
                    )}
                  >
                    <AccordionTrigger className="py-3.5 sm:py-4 hover:no-underline">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                          <DollarSign className="h-3.5 w-3.5 text-coral" />
                          Markup
                        </div>
                        <div className="mt-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                          <h3 className="text-base font-semibold text-slate-950">Choose the client price</h3>
                          <p className="text-sm text-slate-600">{markupSummary}</p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="min-w-0 pb-5">
                      <div className="space-y-4">
                        <div className="grid gap-3 min-[380px]:grid-cols-2 xl:grid-cols-4">
                          {PRICING_TIERS.map((tier) => {
                            const tierPricing = computePricing(budget, tier);
                            const isSelected = selectedTierId === tier.id;
                            return (
                              <button
                                key={tier.id}
                                type="button"
                                onClick={() => {
                                  setSelectedTierId(tier.id);
                                  setCustomSellPrice('');
                                }}
                                className={cn(
                                  'rounded-[1.4rem] border px-3.5 py-3 text-left transition-colors',
                                  isSelected
                                    ? 'border-slate-950 bg-slate-950 text-white'
                                    : 'border-slate-200 bg-white hover:border-slate-300',
                                )}
                              >
                                <p className="text-sm font-semibold">{tier.label}</p>
                                <p className={cn('mt-1 text-xs', isSelected ? 'text-white/70' : 'text-slate-500')}>
                                  {Math.round(tier.markupPct * 100)}% markup
                                </p>
                                <p className="mt-3 text-lg font-semibold">{formatDollars(tierPricing.sellPrice)}</p>
                              </button>
                            );
                          })}

                          <button
                            type="button"
                            onClick={() => setSelectedTierId('custom')}
                            className={cn(
                              'rounded-[1.4rem] border px-3.5 py-3 text-left transition-colors',
                              selectedTierId === 'custom'
                                ? 'border-slate-950 bg-slate-950 text-white'
                                : 'border-slate-200 bg-white hover:border-slate-300',
                            )}
                          >
                            <p className="text-sm font-semibold">Custom</p>
                            <p className={cn('mt-1 text-xs', selectedTierId === 'custom' ? 'text-white/70' : 'text-slate-500')}>
                              Enter your own price
                            </p>
                            <div className="mt-3">
                              {selectedTierId === 'custom' ? (
                                <Input
                                  type="number"
                                  min={budget}
                                  step={50}
                                  value={customSellPrice}
                                  onChange={(event) => setCustomSellPrice(event.target.value)}
                                  onClick={(event) => event.stopPropagation()}
                                  className="h-10 border-white/15 bg-white/10 text-base font-semibold text-white placeholder:text-white/45"
                                  placeholder={`${budget}`}
                                />
                              ) : (
                                <p className="text-lg font-semibold">—</p>
                              )}
                            </div>
                          </button>
                        </div>

                        {customPriceTooLow && (
                          <p className="text-sm text-red-600">
                            Custom sell price cannot be lower than the {formatDollars(budget)} campaign cost.
                          </p>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </div>

          <div className="order-2 min-w-0 space-y-4 xl:sticky xl:top-24 xl:self-start">
            <Card className="hidden border-slate-200 shadow-sm xl:block">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">Offer summary</p>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 rounded-full px-2.5 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                          >
                            <CircleHelp className="mr-1.5 h-3.5 w-3.5" />
                            How pricing works
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" sideOffset={10} className="w-[26rem] rounded-[1.35rem] border-slate-200 p-4 shadow-xl">
                          <PopoverHeader className="mb-3">
                            <PopoverTitle className="text-base text-slate-950">How pricing works</PopoverTitle>
                            <PopoverDescription className="text-sm leading-5 text-slate-600">
                              A quick commercial breakdown for the seller side of the offer.
                            </PopoverDescription>
                          </PopoverHeader>
                          {pricingHelpContent}
                        </PopoverContent>
                      </Popover>
                    </div>
                    <h2 className="mt-2 text-xl font-semibold text-slate-950">{selectedTemplate?.name || 'Campaign offer'}</h2>
                    <p className="mt-1 text-sm text-slate-600">{inventoryTier.shortLabel}</p>
                  </div>
                  <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-xs text-slate-600">
                    {selectedFlight.label}
                  </Badge>
                </div>

                {/* F39/F59 — Seller / Client view toggle. Same data, different
                    framings: Client view hides internal economics so the seller
                    can rehearse the buyer's-eye-view of the offer. */}
                <div className="mt-4 inline-flex rounded-full border border-slate-200 bg-slate-50 p-1 text-xs font-medium">
                  <button
                    type="button"
                    onClick={() => setSummaryView('seller')}
                    className={cn(
                      'rounded-full px-3 py-1 transition-colors',
                      !isClientView ? 'bg-slate-950 text-white' : 'text-slate-600 hover:text-slate-900',
                    )}
                  >
                    Seller view
                  </button>
                  <button
                    type="button"
                    onClick={() => setSummaryView('client')}
                    className={cn(
                      'rounded-full px-3 py-1 transition-colors',
                      isClientView ? 'bg-slate-950 text-white' : 'text-slate-600 hover:text-slate-900',
                    )}
                  >
                    Client view
                  </button>
                </div>

                {isClientView ? (
                  <div className="mt-4 rounded-[1.4rem] border border-slate-200 bg-slate-50 p-4 text-sm leading-5 text-slate-600">
                    What you'd tell the buyer: their <strong className="font-semibold text-slate-950">{formatDollars(pricing.sellPrice)} investment</strong> reaches roughly <strong className="font-semibold text-slate-950">{reachEstimate.estimatedHouseholds.toLocaleString()} households</strong> over <strong className="font-semibold text-slate-950">{selectedFlight.label}</strong>, with <strong className="font-semibold text-slate-950">{reachEstimate.maxImpressions.toLocaleString()} ad views</strong> across {targetSummary.label}.
                  </div>
                ) : (
                  <div className="mt-4 rounded-[1.4rem] border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <span className="text-sm text-slate-500">Max ad views</span>
                      <span className="text-right text-lg font-semibold text-slate-950">{reachEstimate.maxImpressions.toLocaleString()}</span>
                    </div>
                    <Separator className="my-3" />
                    <div className="flex items-start justify-between gap-4">
                      <span className="text-sm text-slate-500">Estimated households</span>
                      <span className="text-right text-lg font-semibold text-slate-950">{reachEstimate.estimatedHouseholds.toLocaleString()}</span>
                    </div>
                    <Separator className="my-3" />
                    <div className="flex items-start justify-between gap-4">
                      <span className="text-sm text-slate-500">Client price</span>
                      <span className="text-right text-lg font-semibold text-slate-950">{formatDollars(pricing.sellPrice)}</span>
                    </div>
                    <Separator className="my-3" />
                    <div className="flex items-start justify-between gap-4">
                      <span className="text-sm text-slate-500">Seller earnings</span>
                      <span className="text-right text-lg font-semibold text-slate-950">{formatDollars(pricing.sellerEarnings)}</span>
                    </div>
                  </div>
                )}

                <div className="mt-4 space-y-3 rounded-[1.4rem] border border-slate-200 bg-slate-50 p-4 text-sm">
                  <div className="flex items-start justify-between gap-4">
                    <span className="text-slate-500">{isClientView ? 'Where it shows' : 'Target area'}</span>
                    <span className="max-w-[15rem] text-right font-medium text-slate-950">{targetSummary.label}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <LabelWithHelp label={isClientView ? 'Ads per day' : 'Daily pacing'} help="How many ad views are projected to deliver each day, on average. Pacing depends on geography, the ad-view price you set, and available streaming inventory." />
                    <span className="font-medium text-slate-950">{reachEstimate.dailyImpressions.toLocaleString()} / day</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <LabelWithHelp label={isClientView ? 'People reached' : 'Unique reach'} help="The estimated number of distinct people who will see the ad at least once. Different from ad views, which counts every time the ad plays." />
                    <span className="font-medium text-slate-950">{reachEstimate.estimatedUniqueReach.toLocaleString()}</span>
                  </div>
                  {!isClientView && (
                    <>
                      <div className="flex items-center justify-between gap-4">
                        <LabelWithHelp label="Campaign cost" help="What the platform spends to deliver the media before any markup. The client price minus campaign cost is your seller earnings." />
                        <span className="font-medium text-slate-950">{formatDollars(pricing.platformCost)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <LabelWithHelp label="Markup" help="What you add on top of campaign cost when setting the client price. It covers your commercial margin." />
                        <span className="font-medium text-slate-950">{Math.round((pricing.markupPct || 0) * 100)}%</span>
                      </div>
                    </>
                  )}
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-slate-500">End date</span>
                    <span className="font-medium text-slate-950">{nextEndDate}</span>
                  </div>
                </div>

                <p className="mt-4 text-sm leading-5 text-slate-600">{reachEstimate.note}</p>

                <Separator className="my-6" />

                <Button
                  onClick={handleContinue}
                  disabled={!canContinue}
                  className="mt-6 h-12 w-full rounded-xl bg-coral text-white hover:bg-coral/90"
                >
                  Build proposal
                  <ArrowRight className="h-4 w-4" />
                </Button>
                {!canContinue && (
                  <p className="mt-3 text-sm text-slate-500">
                    Finish the required location and pricing inputs before continuing.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-white/85 xl:hidden">
          <div className="mx-auto flex max-w-7xl items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-950">{formatDollars(pricing.sellPrice)}</p>
              <p className="truncate text-xs text-slate-500">{selectedTemplate?.name || 'Campaign offer'}</p>
            </div>
            <Button
              onClick={handleContinue}
              disabled={!canContinue}
              className="h-11 min-w-[11rem] rounded-xl bg-coral px-5 text-white hover:bg-coral/90"
            >
              Build proposal
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
